'use client'

import { useCallback } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Container, Grid } from '@mui/material'

// Types
import type { NotFoundPageProps } from '@/types/components/pages/main/not-found'

// Configurations
import Config from '@/config'

// Helpers
import Cookies from '@/helpers/cookies'

// Components
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const TitleTypographyAtom = dynamic(() => import('@/components/atoms/typography/title'))
const ParagraphTypographyAtom = dynamic(() => import('@/components/atoms/typography/paragraph'))
const ErrorIconAtom = dynamic(() => import('@/components/atoms/icon/bulk/error'))
const LinkIconAtom = dynamic(() => import('@/components/atoms/icon/two-tone/link'))

// Page for unknown addresses; it reads the language from the URL, so it needs no cookies and works on static pages
const NotFoundPage = (props: NotFoundPageProps) => {
	// Props
	const { texts, lng: fallback } = props

	// Variables
	const segment = usePathname()?.split('/')[1] ?? ''
	const lng = segment in texts ? segment : fallback
	const text = texts[lng] ?? texts[fallback]

	// Callbacks
	const removeCookies = useCallback(async () => {
		// Remove cookies
		Cookies.remove(`${Config.shortName}-business-alias`)
		Cookies.remove(`${Config.shortName}-business-name`)
		Cookies.remove(`${Config.shortName}-business-branch-code`)
		Cookies.remove(`${Config.shortName}-business-branch-name`)
	}, [])

	return (
		<Grid sx={{ bgcolor: 'background.default' }}>
			<Container maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', height: '100dvh' }}>
				<Grid
					container
					spacing={2}
					sx={{
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						width: 1
					}}
				>
					<Grid>
						<ErrorIconAtom color="primary" sx={{ fontSize: 100 }} />
					</Grid>

					<Grid>
						<TitleTypographyAtom text={text.title} />
					</Grid>

					<Grid>
						<ParagraphTypographyAtom text={text.description} />
					</Grid>

					<Grid>
						<Link href={`/${lng}`}>
							<PrimaryButtonAtom text={text.button} startIcon={<LinkIconAtom />} color="primary" onClick={removeCookies} />
						</Link>
					</Grid>
				</Grid>
			</Container>
		</Grid>
	)
}

export default NotFoundPage
